import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const Pagination = ({ currentPage, totalPages, onPageChange, loading = false }) => {
  const [showDropdown, setShowDropdown] = useState(null); // 'left' or 'right'
  const [inputPage, setInputPage] = useState('');
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(null);
        setInputPage('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePageInput = (e) => {
    e.preventDefault();
    const page = parseInt(inputPage);
    if (page >= 1 && page <= totalPages) {
      onPageChange(page);
      setShowDropdown(null);
      setInputPage('');
    }
  };

  const handleQuickPageSelect = (page) => {
    onPageChange(page);
    setShowDropdown(null);
    setInputPage('');
  };

  // Generate page numbers for dropdown
  const getDropdownPages = (side) => {
    const pages = [];
    if (side === 'left') {
      // Pages between 1 and currentPage - 1
      for (let i = 2; i < currentPage - 1; i++) {
        pages.push(i);
      }
    } else {
      // Pages between currentPage + 1 and totalPages - 1
      for (let i = currentPage + 2; i < totalPages; i++) {
        pages.push(i);
      }
    }
    return pages;
  };

  const renderDropdown = (side) => {
    const pages = getDropdownPages(side);
    
    return (
      <div 
        ref={dropdownRef}
        className="absolute z-50 mt-2 bg-gray-900 border border-white/20 rounded-lg shadow-xl p-3 min-w-[200px]"
        style={{ 
          left: '50%', 
          transform: 'translateX(-50%)',
          top: '100%'
        }}
      >
        {/* Quick input */}
        <form onSubmit={handlePageInput} className="mb-3">
          <label className="text-xs text-gray-400 block mb-1">Aller à la page :</label>
          <div className="flex gap-2">
            <input
              type="number"
              min="1"
              max={totalPages}
              value={inputPage}
              onChange={(e) => setInputPage(e.target.value)}
              placeholder={`1 - ${totalPages}`}
              className="w-full px-3 py-2 bg-gray-800 border border-white/20 rounded text-white text-sm focus:outline-none focus:border-red-500"
              autoFocus
            />
            <Button 
              type="submit"
              size="sm"
              className="bg-red-600 hover:bg-red-700 text-white px-3"
            >
              Go
            </Button>
          </div>
        </form>

        {/* Quick page buttons */}
        {pages.length > 0 && (
          <>
            <div className="text-xs text-gray-400 mb-2">Pages rapides :</div>
            <div className="grid grid-cols-5 gap-1 max-h-[200px] overflow-y-auto">
              {pages.map(page => (
                <button
                  key={page}
                  onClick={() => handleQuickPageSelect(page)}
                  data-testid={`pagination-quick-page-${page}`}
                  className="px-2 py-1 text-sm text-white hover:bg-red-600 rounded transition-colors"
                >
                  {page}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  if (totalPages <= 1) return null;

  return (
    <div className="mt-8 flex items-center justify-center gap-2 sm:gap-4">
      {/* Previous button */}
      <Button
        onClick={() => onPageChange(Math.max(1, currentPage - 1))}
        data-testid="pagination-prev-btn"
        disabled={currentPage === 1 || loading}
        className="bg-white/10 hover:bg-white/20 text-white border border-white/20"
        size="sm"
      >
        <ChevronLeft className="w-4 h-4 mr-1" />
        <span className="hidden sm:inline">Précédent</span>
      </Button>
      
      <div className="flex items-center gap-1 sm:gap-2">
        {/* First page */}
        {currentPage > 2 && (
          <Button
            onClick={() => onPageChange(1)}
            data-testid="pagination-first-btn"
            variant="outline"
            size="sm"
            className="bg-transparent border-white/20 text-white hover:bg-white/10 w-10"
          >
            1
          </Button>
        )}
        
        {/* Left dots with dropdown */}
        {currentPage > 3 && (
          <div className="relative">
            <button
              onClick={() => setShowDropdown(showDropdown === 'left' ? null : 'left')}
              data-testid="pagination-left-dots-btn"
              className="px-2 py-1 text-white hover:text-red-500 hover:bg-white/10 rounded transition-colors cursor-pointer"
              title="Choisir une page"
            >
              •••
            </button>
            {showDropdown === 'left' && renderDropdown('left')}
          </div>
        )}
        
        {/* Previous page */}
        {currentPage > 1 && (
          <Button
            onClick={() => onPageChange(currentPage - 1)}
            data-testid="pagination-page-prev-btn"
            variant="outline"
            size="sm"
            className="bg-transparent border-white/20 text-white hover:bg-white/10 w-10"
          >
            {currentPage - 1}
          </Button>
        )}
        
        {/* Current page */}
        <Button
          variant="default"
          size="sm"
          className="bg-red-600 text-white w-10 cursor-default"
          data-testid="pagination-current-page"
        >
          {currentPage}
        </Button>
        
        {/* Next page */}
        {currentPage < totalPages && (
          <Button
            onClick={() => onPageChange(currentPage + 1)}
            data-testid="pagination-page-next-btn"
            variant="outline"
            size="sm"
            className="bg-transparent border-white/20 text-white hover:bg-white/10 w-10"
          >
            {currentPage + 1}
          </Button>
        )}
        
        {/* Right dots with dropdown */}
        {currentPage < totalPages - 2 && (
          <div className="relative">
            <button
              onClick={() => setShowDropdown(showDropdown === 'right' ? null : 'right')}
              data-testid="pagination-right-dots-btn"
              className="px-2 py-1 text-white hover:text-red-500 hover:bg-white/10 rounded transition-colors cursor-pointer"
              title="Choisir une page"
            >
              •••
            </button>
            {showDropdown === 'right' && renderDropdown('right')}
          </div>
        )}
        
        {/* Last page */}
        {currentPage < totalPages - 1 && (
          <Button
            onClick={() => onPageChange(totalPages)}
            data-testid="pagination-last-btn"
            variant="outline"
            size="sm"
            className="bg-transparent border-white/20 text-white hover:bg-white/10 w-10"
          >
            {totalPages}
          </Button>
        )}
      </div>
      
      {/* Next button */}
      <Button
        onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
        data-testid="pagination-next-btn"
        disabled={currentPage === totalPages || loading}
        className="bg-white/10 hover:bg-white/20 text-white border border-white/20"
        size="sm"
      >
        <span className="hidden sm:inline">Suivant</span>
        <ChevronRight className="w-4 h-4 ml-1" />
      </Button>
    </div>
  );
};

export default Pagination;
